import {
  exportProfessionalInvoiceCsv,
  exportProfessionalInvoiceExcel,
  exportProfessionalInvoicePdf,
  printProfessionalInvoice,
} from '../../utils/professionalExport';
import { formatCurrency } from './receptionistData';
import {
  parsePaymentSplitsFromRow,
  paymentMethodLabel,
} from '../../utils/splitPaymentUtils';
import { withReceiptPrintSerial } from '../../services/receptionist/receiptSerial';
import { cleanSalePackageName } from './sales/saleDurationUtils';
import { saleRowPackageName, saleRowDuration } from './sales/salePeriodUtils';

/** Customer mobile from receipt fields or Guest Pass "Phone: …" note. */
export function resolveReceiptPhone(receipt) {
  const direct = String(
    receipt?.phone
    || receipt?.mobile
    || receipt?.customerPhone
    || '',
  ).trim();
  if (direct && direct !== '—') return direct;
  const note = String(receipt?.paymentNote || receipt?.payment_note || '');
  const match = note.match(/Phone:\s*([^·|]+)/i);
  const fromNote = match ? String(match[1] || '').trim() : '';
  return fromNote && fromNote !== '—' ? fromNote : '';
}

function prepareReceipt(receipt) {
  const base = withReceiptPrintSerial(receipt || {});
  const rawItem = base.item || base.package || '';
  const cleanItem = cleanSalePackageName(rawItem, rawItem || '—');
  const durationLabel = base.durationLabel
    && String(base.durationLabel).trim()
    && String(base.durationLabel).trim() !== '—'
    ? String(base.durationLabel).trim()
    : saleRowDuration({
      package: rawItem,
      duration: base.duration,
      paymentNote: base.paymentNote,
      startDate: base.startDate,
      endDate: base.endDate,
    });
  const phone = resolveReceiptPhone(base);
  return {
    ...base,
    item: cleanItem || '—',
    package: cleanItem || saleRowPackageName(base, cleanItem || '—'),
    durationLabel: durationLabel && durationLabel !== '—' ? durationLabel : (base.durationLabel || '—'),
    phone: phone || '—',
    mobile: phone || '—',
    customerPhone: phone || '—',
  };
}

export function buildSubscriptionReceiptFields(receipt) {
  const complimentary = Boolean(receipt.complimentary);
  const fields = [
    { label: 'CR. No.', value: receipt.invoiceId || '—' },
    { label: 'Payment ID', value: complimentary ? '—' : receipt.paymentId },
    { label: 'Customer', value: receipt.customer },
    { label: 'Mobile', value: resolveReceiptPhone(receipt) || '—' },
    { label: 'Member Serial No', value: receipt.gymId || receipt.customerGymId || receipt.serialNo || '—' },
    { label: 'Package', value: cleanSalePackageName(receipt.package, receipt.package || '—') },
    {
      label: 'Duration',
      value: receipt.durationLabel || saleRowDuration({
        package: receipt.package,
        duration: receipt.duration,
        paymentNote: receipt.paymentNote,
        startDate: receipt.startDate || receipt.start,
        endDate: receipt.endDate || receipt.expiry,
      }),
    },
    {
      label: 'Package Price',
      value: complimentary ? 'Complimentary' : formatCurrency(receipt.packagePrice),
    },
  ];

  if (!complimentary && receipt.discountAmount > 0) {
    fields.push({
      label: `Discount (${receipt.discountPercent}%)`,
      value: `− ${formatCurrency(receipt.discountAmount)}`,
    });
  }

  if (!complimentary && receipt.registrationFee > 0) {
    fields.push({ label: 'Registration Fee', value: formatCurrency(receipt.registrationFee) });
  }

  if (!complimentary && receipt.personalTraining > 0) {
    fields.push({
      label: `Personal Training${receipt.trainer ? ` (${receipt.trainer})` : ''}`,
      value: formatCurrency(receipt.personalTraining),
    });
  }

  if (!complimentary && receipt.classesFee > 0) {
    fields.push({
      label: `Classes${receipt.className ? ` (${receipt.className})` : ''}`,
      value: formatCurrency(receipt.classesFee),
    });
  }

  fields.push(
    { label: 'Start Date', value: receipt.startDate },
    { label: 'End Date', value: receipt.endDate },
    { label: 'Transaction Date', value: receipt.date },
    {
      label: 'Total Amount',
      value: complimentary ? 'Complimentary' : formatCurrency(receipt.finalAmount ?? receipt.totalAmount),
    },
  );

  if (complimentary) {
    fields.push({ label: 'Payment Type', value: 'Complimentary' });
    fields.push({ label: 'Amount Paid', value: 'Complimentary' });
  } else if (receipt.paymentSplits?.length) {
    receipt.paymentSplits.forEach((split) => {
      fields.push({
        label: paymentMethodLabel(split.method),
        value: formatCurrency(split.amount),
      });
    });
    const pending = Number(receipt.totalLeft ?? receipt.pendingAmount) || 0;
    if (pending > 0) {
      fields.push({ label: 'Pending Amount', value: formatCurrency(pending) });
    }
    fields.push({ label: 'Amount Paid', value: formatCurrency(receipt.paymentAmount) });
  } else {
    fields.push({ label: 'Payment Type', value: receipt.paymentType });
    const pending = Number(receipt.totalLeft ?? receipt.pendingAmount) || 0;
    if (pending > 0) {
      fields.push({ label: 'Pending Amount', value: formatCurrency(pending) });
    }
    fields.push({ label: 'Amount Paid', value: formatCurrency(receipt.paymentAmount) });
  }

  if (receipt.paymentNote) {
    fields.push({ label: 'Payment Note', value: receipt.paymentNote });
  }

  return fields;
}

export function buildPaymentReceiptFields(payment) {
  const fields = [
    { label: 'CR. No.', value: payment.invoiceId || '—' },
    { label: 'Payment ID', value: payment.id },
  ];

  fields.push(
    { label: 'Member', value: payment.member },
    { label: 'Mobile', value: resolveReceiptPhone(payment) || '—' },
    { label: 'Package', value: payment.package },
    { label: 'Date', value: payment.date },
    { label: 'Status', value: payment.status === 'completed' ? 'Completed' : 'Pending' },
  );

  const splits = parsePaymentSplitsFromRow(payment);
  if (splits.length) {
    splits.forEach((split) => {
      fields.push({
        label: paymentMethodLabel(split.method),
        value: formatCurrency(split.amount),
      });
    });
  } else {
    fields.push({ label: 'Payment Method', value: payment.method });
  }

  fields.push({ label: 'Amount Paid', value: formatCurrency(payment.amount) });

  if (payment.paymentNote) {
    fields.push({ label: 'Payment Note', value: payment.paymentNote });
  }

  return fields;
}

function getReceiptSlug(receipt) {
  return receipt.receiptSerial || receipt.invoiceId || receipt.id || receipt.paymentId || 'receipt';
}

export function printSubscriptionReceipt(receipt) {
  const prepared = prepareReceipt(receipt);
  printProfessionalInvoice({
    title: 'Invoice & Receipt',
    fields: buildSubscriptionReceiptFields(prepared),
    summary: ['This is a computer-generated receipt and does not require a signature.'],
    terms: true,
    thermal: true,
    copies: 2,
  });
  return prepared;
}

export function exportSubscriptionReceiptPdf(receipt) {
  const prepared = prepareReceipt(receipt);
  exportProfessionalInvoicePdf({
    title: 'Invoice & Receipt',
    fields: buildSubscriptionReceiptFields(prepared),
    summary: ['This is a computer-generated receipt and does not require a signature.'],
  });
  return prepared;
}

export function exportSubscriptionReceiptCsv(receipt) {
  const prepared = prepareReceipt(receipt);
  exportProfessionalInvoiceCsv({
    title: 'Invoice & Receipt',
    fields: buildSubscriptionReceiptFields(prepared),
    slug: getReceiptSlug(prepared),
  });
  return prepared;
}

export function exportSubscriptionReceiptExcel(receipt) {
  const prepared = prepareReceipt(receipt);
  exportProfessionalInvoiceExcel({
    title: 'Invoice & Receipt',
    fields: buildSubscriptionReceiptFields(prepared),
    slug: getReceiptSlug(prepared),
  });
  return prepared;
}

export function buildSaleReceiptFields(receipt) {
  const complimentary = Boolean(receipt.complimentary);
  const remainingInvoice = Boolean(receipt.isRemainingClear);
  const fields = [
    { label: 'CR. No.', value: receipt.invoiceId || '—' },
  ];
  if (!complimentary) {
    fields.push({ label: 'Payment ID', value: receipt.paymentId });
  }
  fields.push(
    { label: 'Sale Type', value: receipt.saleLabel || receipt.saleTitle },
    { label: 'Customer', value: receipt.customer },
    { label: 'Mobile', value: resolveReceiptPhone(receipt) || '—' },
    { label: 'Member Serial No', value: receipt.gymId || '—' },
    {
      label: receipt.itemFieldLabel || (remainingInvoice ? 'Package' : 'Item'),
      value: receipt.item || receipt.package || '—',
    },
    { label: 'Duration', value: receipt.durationLabel || '—' },
    { label: 'Start Date', value: receipt.startDate },
    { label: 'End Date', value: receipt.endDate },
  );

  if (remainingInvoice) {
    fields.push({
      label: 'Sale / Selected Date',
      value: receipt.saleDate || receipt.startDate || '—',
    });
  }

  fields.push(
    {
      label: remainingInvoice ? 'Payment Date' : 'Transaction Date',
      value: receipt.date,
    },
    {
      label: remainingInvoice ? 'Package / Sale Price' : 'Total Amount',
      value: complimentary
        ? 'Complimentary'
        : (receipt.totalAmountDisplay || formatCurrency(receipt.totalAmount)),
    },
  );

  if (remainingInvoice && Number(receipt.packagePrice) > 0) {
    fields.push({
      label: 'Package Price',
      value: receipt.packagePriceDisplay || formatCurrency(receipt.packagePrice),
    });
  }
  if (remainingInvoice && Number(receipt.registrationFee) > 0) {
    fields.push({
      label: 'Registration Fee',
      value: receipt.registrationFeeDisplay || formatCurrency(receipt.registrationFee),
    });
  }

  if (complimentary) {
    fields.push({ label: 'Payment Type', value: 'Complimentary' });
    fields.push({ label: 'Amount Paid', value: 'Complimentary' });
  } else if (receipt.splitLines?.length) {
    receipt.splitLines.forEach((line) => {
      fields.push({ label: line.label, value: line.value });
    });
  } else if (receipt.paymentSplits?.length) {
    receipt.paymentSplits.forEach((split) => {
      fields.push({
        label: paymentMethodLabel(split.method),
        value: formatCurrency(split.amount),
      });
    });
  } else if (receipt.paymentType) {
    fields.push({ label: 'Payment Type', value: receipt.paymentType });
  }

  if (!complimentary && remainingInvoice && receipt.balanceBeforeDisplay) {
    fields.push({ label: 'Remaining Before', value: receipt.balanceBeforeDisplay });
  }

  if (!complimentary && receipt.cumulativePaidDisplay && receipt.installmentDisplay) {
    fields.push({
      label: remainingInvoice ? 'Total Paid (to date)' : 'Total Paid',
      value: receipt.cumulativePaidDisplay,
    });
  }

  if (!complimentary) {
    const pending = Number(receipt.totalLeft ?? receipt.pendingAmount) || 0;
    if (remainingInvoice || pending > 0) {
      fields.push({
        label: remainingInvoice ? 'Remaining After' : 'Pending Amount',
        value: receipt.totalLeftDisplay || formatCurrency(pending),
      });
    }

    fields.push({
      label: remainingInvoice ? 'Remaining Paid' : 'Amount Paid',
      value: receipt.installmentDisplay || receipt.paymentAmountDisplay || formatCurrency(receipt.paymentAmount),
    });
  }

  if (receipt.paymentNote) {
    fields.push({ label: 'Note', value: receipt.paymentNote });
  }

  return fields;
}

function getSaleReceiptTitle(receipt) {
  return receipt?.isRemainingClear ? 'Remaining Payment Invoice' : 'Sale Receipt';
}

function getSaleReceiptSlug(receipt) {
  return receipt.receiptSerial || receipt.invoiceId || receipt.paymentId || 'sale-receipt';
}

export function printSaleReceipt(receipt) {
  const prepared = prepareReceipt(receipt);
  printProfessionalInvoice({
    title: getSaleReceiptTitle(prepared),
    fields: buildSaleReceiptFields(prepared),
    summary: ['This is a computer-generated receipt and does not require a signature.'],
    terms: true,
    thermal: true,
    copies: 2,
  });
  return prepared;
}

export function exportSaleReceiptPdf(receipt) {
  const prepared = prepareReceipt(receipt);
  exportProfessionalInvoicePdf({
    title: getSaleReceiptTitle(prepared),
    fields: buildSaleReceiptFields(prepared),
    summary: ['This is a computer-generated receipt and does not require a signature.'],
  });
  return prepared;
}

export function exportSaleReceiptCsv(receipt) {
  const prepared = prepareReceipt(receipt);
  exportProfessionalInvoiceCsv({
    title: getSaleReceiptTitle(prepared),
    fields: buildSaleReceiptFields(prepared),
    slug: getSaleReceiptSlug(prepared),
  });
  return prepared;
}

export function exportSaleReceiptExcel(receipt) {
  const prepared = prepareReceipt(receipt);
  exportProfessionalInvoiceExcel({
    title: getSaleReceiptTitle(prepared),
    fields: buildSaleReceiptFields(prepared),
    slug: getSaleReceiptSlug(prepared),
  });
  return prepared;
}

export function printPaymentReceipt(payment) {
  const prepared = prepareReceipt(payment);
  printProfessionalInvoice({
    title: 'Payment Receipt',
    fields: buildPaymentReceiptFields(prepared),
    summary: ['Thank you for your payment.'],
    thermal: true,
    copies: 2,
  });
  return prepared;
}

export function exportPaymentReceiptPdf(payment) {
  const prepared = prepareReceipt(payment);
  exportProfessionalInvoicePdf({
    title: 'Payment Receipt',
    fields: buildPaymentReceiptFields(prepared),
    summary: ['Thank you for your payment.'],
  });
  return prepared;
}

export function exportPaymentReceiptCsv(payment) {
  const prepared = prepareReceipt(payment);
  exportProfessionalInvoiceCsv({
    title: 'Payment Receipt',
    fields: buildPaymentReceiptFields(prepared),
    slug: getReceiptSlug(prepared),
  });
  return prepared;
}

export function exportPaymentReceiptExcel(payment) {
  const prepared = prepareReceipt(payment);
  exportProfessionalInvoiceExcel({
    title: 'Payment Receipt',
    fields: buildPaymentReceiptFields(prepared),
    slug: getReceiptSlug(prepared),
  });
  return prepared;
}
